import { ref, useTemplateRef, computed, watch } from 'vue'
import { useDropZone, useFileDialog } from '@vueuse/core'

import type { ComputedRef, Ref } from 'vue'

import { convertBytes } from '@shared/lib/convert-bytes'

import { fileAcceptFormats, fileMaxSize } from '../../config/dropzone'

interface UseDropzoneOptions {
  dropZoneRef?: Ref<HTMLElement | null>
  multiple?: ComputedRef<boolean>
  initialFiles?: Ref<File[] | File | null> | ComputedRef<File[] | File | null>
  onDrop?: (files: File[] | File | null) => void
  onEnter?: (files: File[] | null) => void
  onLeave?: (files: File[] | null) => void
  onOver?: (files: File[] | null) => void
}

interface UploadFile {
  file: File
  error: string | null
}

export const useDropzone = (options: UseDropzoneOptions = {}) => {
  const {
    dropZoneRef: dropZoneRefProp,
    multiple = computed(() => false),
    initialFiles = ref(null),
    onDrop: onDropCallback,
    onEnter: onEnterCallback,
    onLeave: onLeaveCallback,
    onOver: onOverCallback,
  } = options

  const internalDropZoneRef = useTemplateRef<HTMLElement>('dropZoneRef')
  const dropZoneRef = dropZoneRefProp || internalDropZoneRef
  const files = ref<UploadFile[]>([])

  const allowedTypes = computed(() =>
    fileAcceptFormats.split(',').map(t => t.trim().toLowerCase()),
  )

  const validateFile = (file: File): string | null => {
    const lastDot = file.name.lastIndexOf('.')
    const ext = lastDot !== -1 ? file.name.slice(lastDot).toLowerCase() : ''
    const matchesType = allowedTypes.value.some(t => t === ext || file.type === t)

    if (!matchesType) {
      return 'unsupported file type'
    }

    if (file.size > fileMaxSize) {
      return `exceeds ${convertBytes(fileMaxSize)} limit`
    }

    return null
  }

  const handleFiles = (newFiles: File[]): void => {
    const newItems: UploadFile[] = []

    for (const file of newFiles) {
      const error = validateFile(file)
      newItems.push({
        file,
        error,
      })
    }

    if (multiple.value) {
      files.value.push(...newItems)
    } else {
      files.value = newItems.slice(0, 1)
    }
  }

  watch(() => initialFiles.value, (newInitialFiles) => {
    if (newInitialFiles) {
      const arrayFiles = Array.isArray(newInitialFiles) ? newInitialFiles : [newInitialFiles]
      
      const isDifferent = arrayFiles.length !== files.value.length || 
        arrayFiles.some((f, i) => f !== files.value[i]?.file)

      if (isDifferent) {
        files.value = arrayFiles.map(file => ({
          file,
          error: validateFile(file),
        }))
      }
    } else if (files.value.length > 0) {
      files.value = []
    }
  }, { immediate: true })

  const onDrop = (droppedFiles: File[] | null): void => {
    if (droppedFiles) {
      handleFiles(droppedFiles)
      onDropCallback?.(multiple.value ? droppedFiles : (droppedFiles[0] || null))
    } else {
      onDropCallback?.(null)
    }
  }

  const { isOverDropZone } = useDropZone(dropZoneRef, {
    onDrop,
    onEnter: onEnterCallback,
    onLeave: onLeaveCallback,
    onOver: onOverCallback,
  })

  const {
    open: openFileDialog,
    onChange,
    reset,
  } = useFileDialog({
    multiple: multiple.value,
    accept: fileAcceptFormats,
  })

  onChange((selectedFiles) => {
    if (selectedFiles) {
      const arrayFiles = Array.from(selectedFiles)
      handleFiles(arrayFiles)
      onDropCallback?.(multiple.value ? arrayFiles : (arrayFiles[0] || null))
    }
  })

  const resetFiles = (): void => {
    files.value = []
    reset()
  }

  const removeFile = (index: number): void => {
    files.value.splice(index, 1)
    reset()

    if (files.value.length === 0) {
      onDropCallback?.(null)
    } else {
      const remainingFiles = files.value.map(f => f.file)
      onDropCallback?.(multiple.value ? remainingFiles : (remainingFiles[0] || null))
    }
  }

  return {
    dropZoneRef,
    files,
    isOverDropZone,
    handleFiles,
    removeFile,
    resetFiles,
    open: openFileDialog,
  }
}
